import React, { FC, ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';

import { stripePromise } from './stripePromise';
import { appearance, fonts } from './appearance';

interface IJobPaymentElementsProps {
  clientSecret: string | null;
  intentError: string | null;
  onRetry: () => void;
  children: ReactNode;
}

/**
 * Wraps a payment step's fields in a Stripe <Elements> instance.
 *
 * - `intentError` (e.g. "Price has changed") → error banner + Retry.
 * - no `clientSecret` yet → spinner (PaymentIntent still being created by the
 *   flow parent's reachedPayment effect).
 * - otherwise → <Elements> keyed to the clientSecret. The key forces a clean
 *   remount whenever the secret changes (clientSecret is immutable per Elements
 *   instance, so a re-priced intent must mint a fresh <Elements>).
 */
export const JobPaymentElements: FC<IJobPaymentElementsProps> = ({
  clientSecret,
  intentError,
  onRetry,
  children,
}) => {
  if (intentError) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={(
          <Button
            color="inherit"
            size="small"
            onClick={onRetry}
            data-cy="retryIntent"
          >
            Retry
          </Button>
        )}
      >
        {intentError}
      </Alert>
    );
  }

  if (!clientSecret) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      key={clientSecret}
      options={{ clientSecret, appearance, fonts }}
    >
      {children}
    </Elements>
  );
};

export default JobPaymentElements;
